/**
 * "…did all of them wear a helmet?" — the ratio, and by name who did not.
 *
 * ─── PEOPLE, NOT DETECTIONS ───
 * The headline figure counts PEOPLE. A person is detected many times and some frames catch the
 * helmet while others do not, so an event-level ratio ("72% of detections had a helmet") answers
 * a question nobody asked and moves whenever a camera's frame rate changes. Someone counts as
 * compliant if ANY of their sightings shows it, which is the reading a human watching the footage
 * would give. Both figures are shown, with the event one clearly secondary, because they will not
 * agree and the discrepancy is easier to explain once than to field twice.
 *
 * ─── THE NAMES ARE THE POINT ───
 * "14 of 18" is a status; "these four" is something an operator can act on. So the non-compliant
 * side is listed, known people first, with where and when they were last seen — and when the list
 * is capped it says so, because a list that silently ends at the page size reads as a complete
 * roster.
 */
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import type { SearchResult, SearchSplitSubjectRow } from '../../../api/contracts';
import { SegmentedBar } from '../../dashboard/MiniCharts';
import { titleCase } from '../searchModel';
import { AnswerShell, Caveat, Figure, fmtDateTime } from './common';

export interface ComplianceAnswerProps {
  result: SearchResult;
  /** Whether the query was limited to enrolled people — changes what the denominator means. */
  knownOnly: boolean;
  /** The attribute filter the page had to set aside to make the split meaningful, if any. */
  setAsideFilter?: string | null;
}

export default function ComplianceAnswer({ result, knownOnly, setAsideFilter }: ComplianceAnswerProps) {
  const split = result.split_by;
  const subjects = result.subjects ?? 0;
  const withIt = result.subjects_with ?? 0;
  const without = result.subjects_without ?? 0;
  const rows = (result.rows || []) as SearchSplitSubjectRow[];

  if (!split) {
    return (
      <div className="p-8 text-center">
        <ExclamationTriangleIcon className="mx-auto h-6 w-6 text-amber-500" />
        <p className="mt-2 text-sm font-medium text-foreground">Pick what to measure</p>
        <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-muted-foreground">
          Choose a value under “Did all of them have…” above — a helmet, a mask, a hi-vis vest —
          and this becomes how many people had it, and who did not.
        </p>
      </div>
    );
  }

  const thing = titleCase(split.value);
  const who = knownOnly ? 'enrolled people' : 'people';

  if (subjects === 0) {
    return (
      <div className="p-8 text-center">
        <p className="text-sm font-medium text-foreground">Nobody to measure</p>
        <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-muted-foreground">
          {result.camera_scope === 0
            ? 'You do not have access to any camera matching these filters.'
            : `No ${who} matched these filters in this window, so there is no ratio to report. `
              + 'A ratio over nobody would read as 0% compliance.'}
        </p>
      </div>
    );
  }

  const pct = Math.round((withIt / subjects) * 100);
  const all = without === 0;
  const listCapped = rows.length < without;

  return (
    <AnswerShell
      headline={all
        ? `Yes — all ${subjects} ${who} had a ${thing.toLowerCase()}`
        : `No — ${withIt} of ${subjects} ${who} had a ${thing.toLowerCase()}`}
      sub={`${pct}% compliance over the selected window`}
      figures={(
        <>
          <Figure value={`${pct}%`} label={`of ${who} had it`} tone={all ? 'good' : 'bad'} />
          <Figure value={withIt} label="people with it" tone="good" />
          <Figure value={without} label="people without it" tone={all ? 'default' : 'bad'} />
          {/* Secondary on purpose — see the header note on why the two will not agree. */}
          <Figure
            value={`${result.events_with ?? 0}/${result.events ?? 0}`}
            label="detections showing it"
          />
        </>
      )}
      caveats={(
        <>
          <Caveat>
            Counted per PERSON, not per detection: one sighting showing the {thing.toLowerCase()} is
            enough. That is why the detection figure is lower — most frames of the same person do
            not resolve it.
          </Caveat>
          <Caveat>
            {knownOnly
              ? '“Enrolled people” means someone matched to a registered face. It is the closest thing to a staff roster the system holds, not the roster itself — anyone unenrolled is not in this count.'
              : 'Includes unidentified people, each counted by their tracker id — so one stranger seen on two cameras counts twice. Turn on “Known people only” for a per-person figure.'}
          </Caveat>
          {setAsideFilter && (
            <Caveat>
              The {setAsideFilter} filter was set aside for this question — filtering to it and then
              measuring it would always report 100%.
            </Caveat>
          )}
          {listCapped && (
            <Caveat>
              Showing {rows.length} of the {without} without one. Narrow the window or the cameras
              to see the rest.
            </Caveat>
          )}
        </>
      )}
    >
      <div className="rounded-md border border-border bg-card p-3">
        <SegmentedBar
          height={10}
          segments={[
            { key: 'with', label: `With a ${thing.toLowerCase()}`, value: withIt, color: 'var(--brand)' },
            { key: 'without', label: 'Without', value: without, color: 'color-mix(in srgb, var(--muted-foreground) 45%, transparent)' },
          ]}
        />
      </div>

      {rows.length > 0 && (
        <div className="rounded-md border border-border bg-card p-1.5">
          <div className="flex items-center gap-3 border-b border-border px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            <span className="min-w-0 flex-1">Without a {thing.toLowerCase()}</span>
            <span className="w-20 shrink-0 text-right">Sightings</span>
            <span className="w-32 shrink-0 text-right">Last seen</span>
          </div>
          {rows.map((r) => (
            <div key={r.subject} className="flex items-center gap-3 px-2 py-1 text-xs">
              <span className="min-w-0 flex-1 truncate text-foreground">
                {r.subject_label || (
                  <span className="italic text-muted-foreground">
                    Unidentified · tracked only
                  </span>
                )}
              </span>
              <span className="w-20 shrink-0 text-right tabular-nums text-muted-foreground">{r.events}</span>
              <span className="w-32 shrink-0 text-right tabular-nums text-muted-foreground">
                {fmtDateTime(r.last_seen)}
              </span>
            </div>
          ))}
        </div>
      )}
    </AnswerShell>
  );
}
